import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { slugify } from "@/pages/ProductDetail";
import { Button } from "@/components/ui/button";
import { Activity, Waves, ShieldAlert, Music4, Sparkles, Brain, Crown, ArrowRight, Loader2 } from "lucide-react";

const ICONS = [Activity, Waves, ShieldAlert, Music4, Sparkles, Brain, Crown];
const plans = [
  { name: "30 Days", price: "₹5,555" },
  { name: "60 Days", price: "₹9,999" },
  { name: "90 Days", price: "₹12,999" },
];
const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function Products() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/content/products").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
      <div className="max-w-2xl mb-16">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Our Products</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 mb-4">Everything your voice can reveal.</h1>
        <p className="text-muted-foreground leading-relaxed">A complete suite for voice-frequency wellness — from a free first scan to personalised sound-therapy prescriptions.</p>
      </div>

      {items === null ? (
        <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-primary" strokeWidth={1.5} /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {items.map((p, i) => {
            const Icon = ICONS[i % ICONS.length];
            const wide = i === 0;
            return (
              <motion.div key={p.id} {...fade} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }} className={wide ? "lg:col-span-2" : ""}>
                <Link to={`/products/${slugify(p.title)}`} data-testid={`product-${i}`}
                  className="group block h-full rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 hover:-translate-y-1 transition-[transform,border-color]">
                  <div className={`relative overflow-hidden ${wide ? "h-56" : "h-44"}`}>
                    {p.image_url ? (
                      <img src={mediaUrl(p.image_url)} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : <div className="absolute inset-0 bg-secondary" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
                    <span className="absolute left-5 bottom-4 grid place-items-center h-11 w-11 rounded-full bg-primary/90 text-primary-foreground shadow-lg"><Icon strokeWidth={1.5} size={20} /></span>
                  </div>
                  <div className="p-6 sm:p-7">
                    <h3 className="font-serif text-xl mb-2">{p.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-3">{p.description}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">Explore <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" strokeWidth={1.5} /></span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}

      <div className="rounded-3xl border border-border bg-muted/40 grain p-10 sm:p-14">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <h2 className="font-serif text-3xl tracking-tight mb-3">Health Memberships</h2>
            <p className="text-muted-foreground mb-6 max-w-md">Unlock the Emotional and Blood Biomarker reports, the 11-day scan cycle, and 20-minute sound-frequency prescriptions.</p>
            <Link to="/products/health-memberships"><Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold" data-testid="products-view-plans">View membership plans <ArrowRight size={16} strokeWidth={1.5} /></Button></Link>
          </div>
          <div className="md:col-span-6 grid grid-cols-3 gap-3">
            {plans.map((p) => (
              <div key={p.name} className="rounded-xl border border-border bg-card p-5 text-center">
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">{p.name}</p>
                <p className="font-serif text-2xl">{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
